import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Stethoscope, HeartHandshake, HandHeart, Users, Building2, ShieldPlus,
  GraduationCap, Newspaper, ArrowRight, LifeBuoy, BookOpen, Megaphone,
  Phone, AlertTriangle, MapPin, ExternalLink, Eye,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import {
  Container, Section, SectionHeading, Button, AudienceCard,
  Badge, Card, FAQAccordion, EmptyState, LoadingSkeleton, Breadcrumbs, PullQuote,
} from '../../components/portal/ui'
import { HumanArt } from '../../components/portal/HumanArt'
import { usePublishedTestimonials, testimonialAttribution } from '../../components/portal/Voices'
import { AUDIENCES } from '../../components/portal/nav'
import { getPublishedArticles, getCampaigns, type KnowledgeArticle, type Campaign } from '../../services/portalApi'
import { getTopNews, NEWS_CATEGORY_LABELS, type NewsItem } from '../../services/newsService'
import { TranslatedContentNotice } from '../../components/portal/TranslatedContentNotice'
import { FloatingTbAssistant } from '../../components/portal/FloatingTbAssistant'
import { recordVisit } from '../../services/siteVisitService'

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Stethoscope, HeartHandshake, HandHeart, Users, Building2, ShieldPlus, GraduationCap, Newspaper,
}

export default function HomePage() {
  const { t, i18n } = useTranslation()
  const [articles, setArticles] = useState<KnowledgeArticle[]>([])
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  // Total website-visit (footfall) counter. recordVisit() fires once on mount
  // and returns the new grand total; we store it for the small stat strip near
  // the foot of the page. A useRef guard prevents React StrictMode's dev
  // double-invoke from double-counting a single visit (prod counts every load).
  const [totalVisits, setTotalVisits] = useState<number | null>(null)
  const visitFired = useRef(false)
  // Real, consented patient/survivor voices — shown in the Voices strip below the hero.
  const { voices } = usePublishedTestimonials(4)
  const moreVoices = voices.slice(0, 3)

  useEffect(() => {
    let active = true
    // Record this homepage load once per real mount. The guard makes the dev
    // StrictMode double-mount a no-op on the second pass; in production there
    // is no double-mount so every genuine visit is counted. Wrapped in
    // Promise.allSettled-style try/catch so a counter failure never breaks
    // the page rendering.
    if (!visitFired.current) {
      visitFired.current = true
      recordVisit()
        .then((total) => { if (active) setTotalVisits(total) })
        .catch(() => { /* footfall is best-effort — never block the page */ })
    }
    ;(async () => {
      setLoading(true)
      try {
        const [art, camp, nws] = await Promise.allSettled([getPublishedArticles(0, 3), getCampaigns(0, 3), getTopNews(3)])
        if (!active) return
        if (art.status === 'fulfilled') setArticles(art.value.content ?? [])
        if (camp.status === 'fulfilled') setCampaigns(camp.value.content ?? [])
        if (nws.status === 'fulfilled') setNews(nws.value)
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [])

  const faqs = t('pages.home.faq.items', { returnObjects: true }) as { q: string; a: string }[]
  const participationCards = t('pages.home.participation.cards', { returnObjects: true }) as { title: string; desc: string; cta: string }[]
  const iHaveTbLinks = t('pages.home.iHaveTbCard.links', { returnObjects: true }) as string[]
  const supportLinks = t('pages.home.supportCard.links', { returnObjects: true }) as string[]

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: typeof f.a === 'string' ? f.a : '' },
    })),
  }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.home.seo.title')}
        description={t('pages.home.seo.description')}
        canonicalPath="/"
        jsonLd={faqJsonLd}
      />

      {/* ===================== HERO — calm, above-the-fold, action-led =====================
          Fits the first viewport (min-height = viewport − ~100px header) so the primary
          headline + CTAs are visible without scrolling. One clear primary CTA, one
          secondary; supporter/learn journeys are quiet text links, not competing buttons. */}
      <section className="relative overflow-hidden flex items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <Container className="py-10 sm:py-12 lg:py-16 w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Human visual — clearly art, never a fabricated real person */}
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl bg-warm-100/60 border border-warm-200 p-4 sm:p-6">
                <HumanArt variant="joined-hands" className="w-full h-auto max-w-sm mx-auto" />
              </div>
            </div>

            {/* Headline → action */}
            <div className="order-1 lg:order-2 max-w-xl">
              <h1 className="text-hero-h1 font-editorial font-semibold text-snow-900 text-balance">
                {t('pages.home.hero.title')}
              </h1>
              <p className="mt-4 text-hero-lead text-snow-700 text-pretty max-w-xl">
                {t('pages.home.hero.subtitle')}
              </p>

              {/* Primary CTAs — exactly TWO prominent actions, clear hierarchy. */}
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button to="/find-tb-centre" variant="primary" size="lg" icon={<MapPin className="w-5 h-5" />}>
                  {t('pages.home.hero.ctaFindCentre')}
                </Button>
                <Button to="/get-help" variant="secondary" size="lg" icon={<LifeBuoy className="w-5 h-5" />}>
                  {t('pages.home.hero.ctaGetHelp')}
                </Button>
              </div>

              {/* Quiet secondary links — supporter / learn journeys stay discoverable
                  but do NOT visually compete with the primary CTAs. */}
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <Link to="/support-a-patient" className="inline-flex items-center gap-1.5 font-medium text-snow-700 hover:text-[rgb(15_76_129)]">
                  <HandHeart className="w-4 h-4" aria-hidden="true" />
                  {t('pages.home.hero.ctaSupport')}
                </Link>
                <Link to="/become-a-supporter" className="inline-flex items-center gap-1.5 font-medium text-snow-700 hover:text-[rgb(15_76_129)]">
                  <HeartHandshake className="w-4 h-4" aria-hidden="true" />
                  {t('pages.home.hero.ctaSupporter')}
                </Link>
                <Link to="/about-tb" className="inline-flex items-center gap-1.5 font-medium text-snow-700 hover:text-[rgb(15_76_129)]">
                  <BookOpen className="w-4 h-4" aria-hidden="true" />
                  {t('pages.home.hero.ctaLearn')}
                </Link>
              </div>

              {/* Government framing — demoted to a quiet line, not a leading badge */}
              <p className="mt-6 text-xs text-snow-600">
                {t('pages.home.hero.govFraming')}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* ===================== VOICES — more real stories (if any) ===================== */}
      {moreVoices.length > 0 && (
        <Section className="bg-warm-50/70" ariaLabel={t('pages.home.voices.eyebrow')}>
          <Container>
            <SectionHeading
              eyebrow={t('pages.home.voices.eyebrow')}
              title={t('pages.home.voices.title')}
              description={t('pages.home.voices.description')}
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {moreVoices.map((v) => {
                const { author, role } = testimonialAttribution(v)
                return (
                  <PullQuote key={v.id} quote={v.body} author={author} role={role} to="/stories" linkLabel={t('common.readStory')} />
                )
              })}
            </div>
            <div className="mt-8">
              <Button to="/stories" variant="outline" icon={<ArrowRight className="w-4 h-4" />}>
                {t('common.moreStories')}
              </Button>
            </div>
          </Container>
        </Section>
      )}

      {/* ===================== "I AM A..." AUDIENCE GRID ===================== */}
      <Section ariaLabel={t('audiences.title')} className="bg-white/80">
        <Container>
          <SectionHeading
            align="center"
            eyebrow={t('audiences.eyebrow')}
            title={t('audiences.title')}
            description={t('audiences.description')}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {AUDIENCES.map((a) => {
              const Icon = ICONS[a.icon] ?? Users
              const linkLabels = t(`audiences.${a.key}.linkLabels`, { returnObjects: true }) as string[]
              return (
                <AudienceCard
                  key={a.key}
                  to={a.path}
                  tone={a.tone}
                  icon={<Icon className="w-6 h-6" />}
                  title={t(`audiences.${a.key}.label`)}
                  description={t(`audiences.${a.key}.desc`)}
                  links={a.links.map((l, i) => ({ label: linkLabels[i] ?? '', path: l.path }))}
                />
              )
            })}
          </div>
        </Container>
      </Section>

      {/* ===================== KEY MESSAGE ===================== */}
      <Section className="bg-[rgb(240_252_255)]/60">
        <Container>
          <Card className="p-8 sm:p-10 border-0 bg-gradient-to-br from-[rgb(232_241_252)] to-[rgb(240_252_255)]">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <h2 className="text-2xl font-bold text-snow-900 mb-3">{t('pages.home.keyMessage.title')}</h2>
                <p className="text-snow-600">
                  {t('pages.home.keyMessage.body')}
                </p>
              </div>
              <div className="flex md:justify-end">
                <Button to="/i-have-tb" variant="primary" icon={<ArrowRight className="w-4 h-4" />}>
                  {t('pages.home.keyMessage.cta')}
                </Button>
              </div>
            </div>
          </Card>
        </Container>
      </Section>

      {/* ===================== I HAVE TB + SUPPORT ===================== */}
      <Section className="bg-white/80">
        <Container>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-8">
              <span className="w-12 h-12 rounded-xl bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center mb-4" aria-hidden="true">
                <Stethoscope className="w-6 h-6" />
              </span>
              <h2 className="text-xl font-bold text-snow-900 mb-2">{t('pages.home.iHaveTbCard.title')}</h2>
              <p className="text-snow-600 mb-5">
                {t('pages.home.iHaveTbCard.body')}
              </p>
              <ul className="space-y-2 mb-5 text-sm">
                {iHaveTbLinks.map((l, i) => (
                  <li key={i}>
                    <Link to="/i-have-tb" className="flex items-center gap-1.5 text-snow-700 hover:text-[rgb(15_76_129)]">
                      <ArrowRight className="w-3.5 h-3.5 text-snow-300" aria-hidden="true" />
                      {l}
                    </Link>
                  </li>
                ))}
              </ul>
              <Button to="/i-have-tb" variant="primary">{t('pages.home.iHaveTbCard.cta')}</Button>
            </Card>
            <Card className="p-8">
              <span className="w-12 h-12 rounded-xl bg-[rgb(232_252_243)] text-[rgb(0_138_88)] flex items-center justify-center mb-4" aria-hidden="true">
                <HandHeart className="w-6 h-6" />
              </span>
              <h2 className="text-xl font-bold text-snow-900 mb-2">{t('pages.home.supportCard.title')}</h2>
              <p className="text-snow-600 mb-5">
                {t('pages.home.supportCard.body')}
              </p>
              <ul className="space-y-2 mb-5 text-sm">
                {supportLinks.map((l, i) => (
                  <li key={i}>
                    <Link to="/support-a-patient" className="flex items-center gap-1.5 text-snow-700 hover:text-[rgb(0_138_88)]">
                      <ArrowRight className="w-3.5 h-3.5 text-snow-300" aria-hidden="true" />
                      {l}
                    </Link>
                  </li>
                ))}
              </ul>
              <Button to="/support-a-patient" variant="secondary">{t('pages.home.supportCard.cta')}</Button>
            </Card>
          </div>
        </Container>
      </Section>

      {/* ===================== PARTICIPATION ===================== */}
      <Section className="bg-snow-50/75">
        <Container>
          <SectionHeading
            eyebrow={t('pages.home.participation.eyebrow')}
            title={t('pages.home.participation.title')}
            description={t('pages.home.participation.description')}
          />
          <div className="grid sm:grid-cols-3 gap-5">
            {participationCards.map((card, i) => {
              const Icon = [Users, Building2, Megaphone][i] ?? Users
              const cls = [
                'text-[rgb(0_152_186)]',
                'text-snow-700',
                'text-[rgb(0_138_88)]',
              ][i]
              const to = ['/get-help', '/support-a-patient', '/campaigns'][i] ?? '/get-help'
              return (
                <Card key={i} hover className="p-6">
                  <Icon className={`w-7 h-7 ${cls} mb-3`} aria-hidden="true" />
                  <h3 className="font-bold text-snow-900 mb-1">{card.title}</h3>
                  <p className="text-sm text-snow-600 mb-4">{card.desc}</p>
                  <Button to={to} variant="outline" size="sm">{card.cta}</Button>
                </Card>
              )
            })}
          </div>
        </Container>
      </Section>

      {/* ===================== CAMPAIGNS + STORIES ===================== */}
      <Section className="bg-snow-50/75">
        <Container>
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Campaigns */}
            <div>
              <SectionHeading eyebrow={t('pages.home.campaigns.eyebrow')} title={t('pages.home.campaigns.title')} as="h2" />
              {loading ? (
                <LoadingSkeleton count={2} />
              ) : campaigns.length ? (
                <div className="space-y-4">
                  {campaigns.map((c) => (
                    <Card key={c.id} hover className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Badge tone="blue" className="mb-2">{c.campaignType?.replace(/_/g, ' ')}</Badge>
                          <h3 className="font-semibold text-snow-900">{c.title}</h3>
                          {c.location && <p className="text-xs text-snow-500 mt-1">{c.location}</p>}
                        </div>
                      </div>
                      {c.description && <p className="text-sm text-snow-600 mt-2 line-clamp-2">{c.description}</p>}
                      <Link to={`/campaigns/${c.id}`} className="text-sm font-medium text-[rgb(15_76_129)] hover:underline mt-3 inline-block">
                        {t('pages.home.campaigns.viewCampaign')}
                      </Link>
                    </Card>
                  ))}
                </div>
              ) : (
                <EmptyState icon={<Megaphone className="w-7 h-7" />} title={t('pages.home.campaigns.emptyTitle')}
                  description={t('pages.home.campaigns.emptyDesc')} />
              )}
              <Link to="/campaigns" className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-[rgb(15_76_129)] hover:underline">
                {t('pages.home.campaigns.allLink')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Stories / latest knowledge */}
            <div>
              <SectionHeading eyebrow={t('pages.home.latest.eyebrow')} title={t('pages.home.latest.title')} as="h2" />
              {loading ? (
                <LoadingSkeleton count={2} />
              ) : articles.length ? (
                <div className="space-y-4">
                  {articles.some((a) => a.matchedLanguage && a.matchedLanguage !== (i18n.language?.split('-')[0] || 'en')) && (
                    <TranslatedContentNotice matchedLanguage="en" />
                  )}
                  {articles.map((a) => (
                    <Card key={a.id} hover className="p-5">
                      <Badge tone="green" className="mb-2">{a.categoryName ?? t('common.knowledge', 'Knowledge')}</Badge>
                      <h3 className="font-semibold text-snow-900">{a.title}</h3>
                      {a.excerpt && <p className="text-sm text-snow-600 mt-2 line-clamp-2">{a.excerpt}</p>}
                      <Link to={`/knowledge`} className="text-sm font-medium text-[rgb(15_76_129)] hover:underline mt-3 inline-block">
                        {t('pages.home.latest.readMore')}
                      </Link>
                    </Card>
                  ))}
                </div>
              ) : (
                <EmptyState icon={<BookOpen className="w-7 h-7" />} title={t('pages.home.latest.emptyTitle')}
                  description={t('pages.home.latest.emptyDesc')} />
              )}
              <Link to="/stories" className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-[rgb(15_76_129)] hover:underline">
                {t('pages.home.latest.allLink')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* ===================== FAQ ===================== */}
      <Section className="bg-white/80">
        <Container>
          <SectionHeading align="center" eyebrow={t('pages.home.faq.eyebrow')} title={t('pages.home.faq.title')} />
          <div className="max-w-3xl mx-auto">
            <FAQAccordion items={faqs} />
          </div>
          <div className="text-center mt-8">
            <Button to="/get-help" variant="outline" icon={<Phone className="w-4 h-4" />}>
              {t('pages.home.faq.cta')}
            </Button>
          </div>
        </Container>
      </Section>

      {/* ===================== NEWS & UPDATES (compact, non-dominating) =====================
          Deliberately placed LOW on the page so the homepage stays action-led.
          A small curated strip (max 3 admin-curated, agenda-scoped items) — each
          links to a real official programme page. No fabricated content. */}
      {news.length > 0 && (
        <Section className="bg-snow-50/75">
          <Container>
            <div className="flex items-end justify-between gap-4 mb-5">
              <div className="max-w-2xl">
                <p className="text-xs font-semibold uppercase tracking-wider text-snow-500">{t('pages.home.news.eyebrow')}</p>
                <h2 className="text-xl font-bold text-snow-900 mt-1">{t('pages.home.news.title')}</h2>
                <p className="text-sm text-snow-600 mt-1">{t('pages.home.news.description')}</p>
              </div>
              <Link to="/news" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[rgb(15_76_129)] hover:underline shrink-0">
                {t('pages.home.news.allLink')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {news.map((n) => (
                <Card key={n.id} className="p-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge tone="slate">{NEWS_CATEGORY_LABELS[n.category] ?? n.category}</Badge>
                    {n.isPinned && <Badge tone="teal">{t('pages.home.news.pinned')}</Badge>}
                  </div>
                  <h3 className="font-semibold text-snow-900 text-sm leading-snug line-clamp-2">{n.title}</h3>
                  {n.summary && <p className="text-xs text-snow-600 mt-1.5 line-clamp-3 flex-1">{n.summary}</p>}
                  <div className="mt-3 pt-3 border-t border-snow-100 flex items-center justify-between gap-2">
                    <span className="text-[11px] text-snow-500 truncate">
                      {n.publishedAt ? new Date(n.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                      {n.source ? ` · ${n.source}` : ''}
                    </span>
                    {n.url && (
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-[rgb(15_76_129)] hover:underline shrink-0">
                        {t('pages.home.news.readOfficial')} <ExternalLink className="w-3 h-3" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </Card>
              ))}
            </div>
            <Link to="/news" className="inline-flex sm:hidden items-center gap-1 mt-4 text-sm font-medium text-[rgb(15_76_129)] hover:underline">
              {t('pages.home.news.allLink')} <ArrowRight className="w-4 h-4" />
            </Link>
          </Container>
        </Section>
      )}

      {/* ===================== TOTAL VISITS (footfall) =====================
          Slim, non-intrusive stat strip showing the running total of homepage
          loads. The count is best-effort (recorded once on mount); a failure to
          fetch never blocks the page — the strip simply stays hidden. */}
      {totalVisits !== null && (
        <Section className="bg-white/80 pt-0 pb-6">
          <Container>
            <div className="flex items-center justify-center gap-2 text-sm text-snow-500">
              <Eye className="w-4 h-4" aria-hidden="true" />
              <span>
                {t('pages.home.visits.label', { defaultValue: 'Total website visits' })}
                <span className="ml-1.5 font-semibold text-snow-700">
                  {totalVisits.toLocaleString()}
                </span>
              </span>
            </div>
          </Container>
        </Section>
      )}

      {/* ===================== EMERGENCY STRIP ===================== */}
      <Section className="bg-[rgb(255_251_235)]/80 pt-0 pb-16">
        <Container>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl bg-[rgb(254_243_199)] border border-[rgb(254_224_139)] p-6">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[rgb(185_83_9)] shrink-0" aria-hidden="true" />
              <p className="text-[rgb(120_53_15)] font-medium">
                {t('pages.home.emergency.body')}
              </p>
            </div>
            <a href="tel:1800116666" className="btn-primary shrink-0 flex items-center gap-2">
              <Phone className="w-4 h-4" aria-hidden="true" />
              {t('pages.home.emergency.call')}
            </a>
          </div>
        </Container>
      </Section>

      {/* Floating TB Assistant — reuses the existing controlled assistant
          (askAssistant + getAssistantTopics). Position: fixed, so it never
          affects page layout height. Secondary to the homepage's primary CTAs. */}
      <FloatingTbAssistant />
    </PortalLayout>
  )
}